import { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, query, orderByChild, equalTo, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';

// Hook für einzelne Course-Daten (von Products mit type='course')
export const useCourseData = (courseId, forceRefresh = 0) => {
    const [courseData, setCourseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('🚀 useCourseData - Effect triggered for courseId:', courseId);
        
        if (!courseId) {
            console.log('useCourseData - No courseId provided, resetting state');
            setCourseData(null);
            setLoading(false);
            setError(null);
            return;
        }

        // Reset states when courseId changes
        console.log('🔄 useCourseData - Resetting states for:', courseId);
        setLoading(true);
        setError(null);
        setCourseData(null);

        const db = getDatabase();
        const courseRef = ref(db, `products/${courseId}`);
        console.log('🎯 useCourseData - Setting up NEW listener for:', courseRef.toString());

        // Force immediate read first, then listen for changes
        console.log('📖 useCourseData - Forcing immediate read for:', courseId);
        
        get(courseRef).then((snapshot) => {
            console.log('⚡ useCourseData - Immediate read completed for:', courseId);
            if (snapshot.exists()) {
                const rawData = snapshot.val();
                
                // Validate that this is actually a course
                if (rawData.type !== 'course') {
                    console.log('❌ useCourseData - Product is not a course:', rawData.type);
                    setCourseData(null);
                    setError('Selected product is not a course');
                    setLoading(false);
                    return;
                }
                
                const immediateData = { id: courseId, ...rawData };
                console.log('⚡ useCourseData - Immediate course data:', immediateData);
                setCourseData(immediateData);
                setError(null);
            } else {
                setCourseData(null);
                setError('Course not found');
            }
            setLoading(false);
        }).catch((error) => {
            console.error('⚡ useCourseData - Immediate read error:', error);
            setError('Failed to load course');
            setLoading(false);
        });

        const unsubscribe = onValue(courseRef, 
            (snapshot) => {
                console.log('🔥 useCourseData - Firebase callback triggered for:', courseId, {
                    exists: snapshot.exists(),
                    timestamp: new Date().toISOString()
                });
                
                if (snapshot.exists()) {
                    const rawData = snapshot.val();
                    
                    // Validate that this is actually a course
                    if (rawData.type !== 'course') {
                        console.log('❌ useCourseData - Product is not a course:', rawData.type);
                        setCourseData(null);
                        setError('Selected product is not a course');
                        setLoading(false);
                        return;
                    }
                    
                    const newCourseData = { id: courseId, ...rawData };
                    
                    console.log('✅ useCourseData - Firebase course data received:', courseId);
                    console.log('📊 useCourseData - Course content data:', {
                        modulesCount: newCourseData.contentData?.videoModules?.length || 0,
                        subtype: newCourseData.subtype,
                        accessDuration: newCourseData.accessDuration
                    });
                    
                    setCourseData(newCourseData);
                    setError(null);
                    console.log('✅ useCourseData - State updated successfully for:', courseId);
                } else {
                    console.log('❌ useCourseData - Course not found:', courseId);
                    setCourseData(null);
                    setError('Course not found');
                }
                
                setLoading(false);
                console.log('🏁 useCourseData - Loading set to false for:', courseId);
            },
            (error) => {
                console.error('useCourseData - Firebase error for course:', courseId, error);
                setError('Failed to load course');
                setLoading(false);
            }
        );

        return () => {
            console.log('🧹 useCourseData - Cleaning up listener for:', courseId);
            unsubscribe();
        };
    }, [courseId, forceRefresh]);

    return { courseData, loading, error };
};

// Hook für alle User-Courses (Courses sind Products mit type='course')
export const useUserCourses = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const auth = getAuth();
    const currentUser = auth.currentUser;

    useEffect(() => {
        if (!currentUser) {
            setCourses([]);
            setLoading(false);
            return;
        }

        const db = getDatabase();
        const productsQuery = query(
            ref(db, 'products'),
            orderByChild('providerId'),
            equalTo(currentUser.uid)
        );

        const unsubscribe = onValue(productsQuery,
            (snapshot) => {
                const coursesData = [];
                snapshot.forEach((childSnapshot) => {
                    const productData = childSnapshot.val();
                    // Nur Courses hinzufügen
                    if (productData.type === 'course') {
                        coursesData.push({
                            id: childSnapshot.key,
                            ...productData
                        });
                    }
                });
                
                // Sortiere nach Erstellungsdatum (neueste zuerst)
                coursesData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                
                setCourses(coursesData);
                setError(null);
                setLoading(false);
            },
            (error) => {
                console.error('Error loading user courses:', error);
                setError('Failed to load courses');
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [currentUser]);

    return { courses, loading, error };
};

// Helper function für Course-Status
export const getCourseStatusInfo = (status) => {
    const statusConfig = {
        draft: { 
            label: 'Draft', 
            className: 'status-draft', 
            color: '#6b7280',
            icon: '📝' 
        },
        published: { 
            label: 'Published', 
            className: 'status-published', 
            color: '#10b981',
            icon: '✅' 
        },
        archived: { 
            label: 'Archived', 
            className: 'status-archived', 
            color: '#f59e0b',
            icon: '📦' 
        }
    };
    
    return statusConfig[status] || statusConfig.draft;
};

// Helper function für Course-Type Info
export const getCourseTypeInfo = (subtype) => {
    const typeConfig = {
        online_course: {
            label: 'Online Course',
            icon: 'fas fa-graduation-cap',
            color: '#10b981',
            description: 'Comprehensive online learning'
        },
        mini_course: {
            label: 'Mini Course',
            icon: 'fas fa-play-circle',
            color: '#3b82f6',
            description: 'Quick learning sessions'
        },
        masterclass: {
            label: 'Masterclass',
            icon: 'fas fa-crown',
            color: '#8b5cf6',
            description: 'Expert-level training'
        },
        certification: {
            label: 'Certification',
            icon: 'fas fa-certificate',
            color: '#f59e0b',
            description: 'Professional certification'
        },
        workshop_series: {
            label: 'Workshop Series',
            icon: 'fas fa-tools',
            color: '#ef4444',
            description: 'Hands-on workshop series'
        }
    };
    
    return typeConfig[subtype] || {
        label: 'Video Course',
        icon: 'fas fa-play',
        color: '#10b981',
        description: 'Video-based learning'
    };
};

// Helper function für Module-Duration Formatting
export const formatModuleDuration = (duration) => {
    if (!duration) return '';
    
    // Assume duration is in format "45 min" or "1h 30min" etc.
    return duration;
};

// Helper function für Total Course Duration
export const calculateTotalDuration = (videoModules) => {
    if (!videoModules || !Array.isArray(videoModules)) return '0 min';
    
    let totalMinutes = 0;
    
    videoModules.forEach(module => {
        if (module.duration) {
            // Simple parsing - assume format like "45 min", "1h 30min", etc.
            const duration = module.duration.toLowerCase();
            
            // Extract minutes
            const minMatch = duration.match(/(\d+)\s*min/);
            if (minMatch) {
                totalMinutes += parseInt(minMatch[1]);
            }
            
            // Extract hours and convert to minutes
            const hourMatch = duration.match(/(\d+)\s*h/);
            if (hourMatch) {
                totalMinutes += parseInt(hourMatch[1]) * 60;
            }
        }
    });
    
    if (totalMinutes === 0) return 'Duration TBD';
    
    if (totalMinutes < 60) {
        return `${totalMinutes} min`;
    } else {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
    }
};

